import React, { useEffect, useState } from "react";
import { API_BASE } from "../../api";
import "./AdminOfflineEnrollments.css";
import * as XLSX from "xlsx";

const AdminOfflineEnrollments = ({ isDashboardView = false }) => {
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterActivity, setFilterActivity] = useState("all");
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Form State
    const initialFormState = {
        FullName: "",
        FatherName: "",
        Gender: "",
        DOB: "",
        Address: "",
        SchoolName: "",
        Religion: "",
        Phone: "",
        City: "",
        Activity: "",
        Amount: "",
        PaymentMode: "",
        CollectedBy: "",
        TransactionId: "",
        Branch: "",
        AadharNumber: "",
        JoiningDate: new Date().toISOString().split('T')[0],
        MedicalIssue: false,
        IssueType: ""
    };
    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchEnrollments();
    }, []);

    const fetchEnrollments = async () => {
        setLoading(true);
        const token = localStorage.getItem("token");
        try {
            const response = await fetch(`${API_BASE}/api/offline-enrollments`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await response.json();
            setEnrollments(data);
        } catch (error) {
            console.error("Error fetching offline enrollments:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.FullName) {
            alert("Full Name is required");
            return;
        }

        const token = localStorage.getItem("token");
        const method = editingId ? "PUT" : "POST";
        const url = editingId 
            ? `${API_BASE}/api/offline-enrollments/${editingId}` 
            : `${API_BASE}/api/offline-enrollments`;

        try {
            const response = await fetch(url, {
                method,
                headers: { 
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}` 
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                alert(editingId ? "Updated successfully!" : "Saved successfully!");
                setIsFormOpen(false);
                setEditingId(null);
                setFormData(initialFormState);
                fetchEnrollments();
            } else {
                const error = await response.json();
                alert("Error: " + error.message);
            }
        } catch (error) {
            console.error("Error saving enrollment:", error);
        }
    };

    const handleEdit = (en) => {
        setEditingId(en.Id);
        // Pre-fill form (handle date formatting if needed)
        const formattedData = { ...en };
        if (en.DOB) formattedData.DOB = new Date(en.DOB).toISOString().split('T')[0];
        if (en.JoiningDate) formattedData.JoiningDate = new Date(en.JoiningDate).toISOString().split('T')[0];
        
        setFormData(formattedData);
        setIsFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this enrollment?")) return;

        const token = localStorage.getItem("token");
        try {
            const response = await fetch(`${API_BASE}/api/offline-enrollments/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                alert("Deleted successfully");
                fetchEnrollments();
            }
        } catch (error) {
            console.error("Error deleting:", error);
        }
    };

    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredEnrollments);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "OfflineEnrollments");
        XLSX.writeFile(workbook, "OfflineEnrollments.xlsx");
    };

    const filteredEnrollments = enrollments.filter(en => {
        const matchesSearch = 
            en.FullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
            (en.Phone && en.Phone.includes(searchTerm));
        const matchesActivity = filterActivity === "all" || en.Activity === filterActivity;
        return matchesSearch && matchesActivity;
    });

    const activities = [...new Set(enrollments.map(e => e.Activity).filter(a => a))];

    return (
        <div className="offline-enrollments-wrapper">
            <div className="admin-header-section">
                <div className="admin-title-box">
                    <h1>Offline Enrollments Update</h1>
                    <p style={{ color: 'var(--admin-text-light)', marginTop: '5px' }}>Manage walk-in registrations and manual entries</p>
                </div>
                <button onClick={() => { setIsFormOpen(!isFormOpen); setEditingId(null); setFormData(initialFormState); }} className="add-new-btn">
                    {isFormOpen ? "Close Form" : <><span style={{ marginRight: '8px' }}>+</span> Add New Enrollment</>}
                </button>
            </div>

            {isFormOpen && (
                <div className="offline-form-container">
                    <form onSubmit={handleSave}>
                        <div className="form-section">
                            <h3>👤 Personal Information</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Full Name *</label>
                                    <input type="text" name="FullName" value={formData.FullName} onChange={handleInputChange} className="required" placeholder="Enter student name" />
                                </div>
                                <div className="form-group">
                                    <label>Father Name</label>
                                    <input type="text" name="FatherName" value={formData.FatherName} onChange={handleInputChange} placeholder="Father's full name" />
                                </div>
                                <div className="form-group">
                                    <label>Gender</label>
                                    <select name="Gender" value={formData.Gender} onChange={handleInputChange}>
                                        <option value="">Select Gender</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Date of Birth</label>
                                    <input type="date" name="DOB" value={formData.DOB} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>Phone</label>
                                    <input type="text" name="Phone" value={formData.Phone} onChange={handleInputChange} placeholder="Contact number" />
                                </div>
                                <div className="form-group">
                                    <label>Aadhar Number</label>
                                    <input type="text" name="AadharNumber" value={formData.AadharNumber} onChange={handleInputChange} placeholder="12 digit Aadhar" />
                                </div>
                                <div className="form-group">
                                    <label>Address</label>
                                    <input type="text" name="Address" value={formData.Address} onChange={handleInputChange} placeholder="Full address" />
                                </div>
                                <div className="form-group">
                                    <label>City</label>
                                    <input type="text" name="City" value={formData.City} onChange={handleInputChange} placeholder="City" />
                                </div>
                                <div className="form-group">
                                    <label>Religion</label>
                                    <input type="text" name="Religion" value={formData.Religion} onChange={handleInputChange} placeholder="Religion" />
                                </div>
                                <div className="form-group">
                                    <label>School Name</label>
                                    <input type="text" name="SchoolName" value={formData.SchoolName} onChange={handleInputChange} placeholder="Current school" />
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>💳 Activity & Payment</h3>
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>Activity</label>
                                    <select name="Activity" value={formData.Activity} onChange={handleInputChange}>
                                        <option value="">Select Activity</option>
                                        <option value="Summer Camp">Summer Camp</option>
                                        <option value="Winter Camp">Winter Camp</option>
                                        <option value="Picnic">Picnic</option>
                                        <option value="Sports Meet">Sports Meet</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Branch</label>
                                    <input type="text" name="Branch" value={formData.Branch} onChange={handleInputChange} placeholder="Store/Branch location" />
                                </div>
                                <div className="form-group">
                                    <label>Joining Date</label>
                                    <input type="date" name="JoiningDate" value={formData.JoiningDate} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>Amount</label>
                                    <input type="number" name="Amount" value={formData.Amount} onChange={handleInputChange} placeholder="0.00" />
                                </div>
                                <div className="form-group">
                                    <label>Payment Mode</label>
                                    <select name="PaymentMode" value={formData.PaymentMode} onChange={handleInputChange}>
                                        <option value="">Select Mode</option>
                                        <option value="Cash">Cash</option>
                                        <option value="Online">Online</option>
                                    </select>
                                </div>

                                {formData.PaymentMode === 'Cash' && (
                                    <div className="form-group">
                                        <label>Collected By</label>
                                        <input type="text" name="CollectedBy" value={formData.CollectedBy} onChange={handleInputChange} placeholder="Staff name" />
                                    </div>
                                )}

                                {formData.PaymentMode === 'Online' && (
                                    <div className="form-group">
                                        <label>Transaction ID</label>
                                        <input type="text" name="TransactionId" value={formData.TransactionId} onChange={handleInputChange} placeholder="UTR/Ref Number" />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>🏥 Medical Information</h3>
                            <div className="form-grid">
                                <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: '15px' }}>
                                    <input type="checkbox" name="MedicalIssue" checked={formData.MedicalIssue} onChange={handleInputChange} style={{ width: '20px', height: '20px' }} />
                                    <label style={{ margin: 0 }}>Has any medical issue?</label>
                                </div>
                                {formData.MedicalIssue && (
                                    <div className="form-group">
                                        <label>Issue Type / Description</label>
                                        <input type="text" name="IssueType" value={formData.IssueType} onChange={handleInputChange} placeholder="Describe medical condition" />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-actions">
                            <button type="button" onClick={() => setIsFormOpen(false)} className="btn-cancel">Cancel</button>
                            <button type="submit" className="btn-save">{editingId ? "Update Enrollment" : "Save Enrollment"}</button>
                        </div>
                    </form>
                </div>
            )}

            {!isFormOpen && (
                <>
                    <div className="search-bar">
                        <input 
                            type="text" 
                            className="search-input" 
                            placeholder="Search by name or phone..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select className="search-input" style={{ flex: '0 0 200px' }} value={filterActivity} onChange={(e) => setFilterActivity(e.target.value)}>
                            <option value="all">All Activities</option>
                            {activities.map(a => <option key={a} value={a}>{a}</option>)}
                        </select>
                        <button onClick={handleExportExcel} className="export-excel-btn">📊 Export Excel</button>
                    </div>

                    <div className="table-card">
                        <table className="custom-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Phone</th>
                                    <th>Activity</th>
                                    <th>Amount</th>
                                    <th>Mode</th>
                                    <th>Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '50px' }}>Loading...</td></tr>
                                ) : filteredEnrollments.length === 0 ? (
                                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '50px' }}>No records found</td></tr>
                                ) : filteredEnrollments.map(en => (
                                    <tr key={en.Id}>
                                        <td style={{ fontWeight: '700' }}>{en.FullName}</td>
                                        <td>{en.Phone || "-"}</td>
                                        <td><span className="card-code" style={{ fontSize: '0.8rem' }}>{en.Activity || "-"}</span></td>
                                        <td style={{ fontWeight: '700' }}>₹{Number(en.Amount || 0).toLocaleString()}</td>
                                        <td>
                                            <span className={`card-status-badge ${en.PaymentMode === 'Online' ? 'status-paid' : 'status-pending'}`} style={{ position: 'static' }}>
                                                {en.PaymentMode || "-"}
                                            </span>
                                        </td>
                                        <td>{en.JoiningDate ? new Date(en.JoiningDate).toLocaleDateString() : "-"}</td>
                                        <td>
                                            <div className="action-btns">
                                                <button className="action-btn btn-edit" onClick={() => handleEdit(en)} title="Edit">✏️</button>
                                                <button className="action-btn btn-delete" onClick={() => handleDelete(en.Id)} title="Delete">🗑️</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminOfflineEnrollments;
